import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import FitnessCenterRoundedIcon from "@mui/icons-material/FitnessCenterRounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { motion, AnimatePresence } from "framer-motion";

const MotionBox = motion(Box);

const plans = [
  {
    id: 1,
    title: "PASE DIARIO",
    price: "$3,00",
    duration: "1 día",
    choices: ["Cross", "Funcional", "Máquinas"],
    summary: "Ideal para probar el ambiente de NovaXFit sin compromiso.",
    detailOne: "Puedes escoger la modalidad que más te guste ese día.",
    detailTwo: "Acceso práctico para entrenar cuando lo necesites.",
  },
  {
    id: 2,
    title: "TARJETA 12 DÍAS",
    price: "$25,00",
    duration: "12 días",
    choices: ["Cross", "Funcional", "Máquinas"],
    summary: "Perfecta para quienes buscan flexibilidad durante el mes.",
    detailOne: "Entrena a tu ritmo usando los días incluidos de forma cómoda.",
    detailTwo: "Una opción dinámica para combinar disciplina y libertad.",
  },
  {
    id: 3,
    title: "MEDIA MENSUALIDAD",
    price: "$20,00",
    duration: "15 días",
    choices: ["Cross", "Funcional", "Máquinas"],
    summary: "Una alternativa práctica para comenzar con constancia.",
    detailOne: "Escoge la modalidad que mejor se adapte a tu objetivo.",
    detailTwo: "Excelente para retomar entrenamiento o iniciar con orden.",
  },
  {
    id: 4,
    title: "MENSUALIDAD",
    price: "$35,00",
    duration: "30 días",
    choices: ["Cross", "Funcional", "Máquinas"],
    summary: "Entrenamiento continuo con una propuesta clara y versátil.",
    detailOne: "Puedes enfocarte en cross, funcional o trabajo en máquinas.",
    detailTwo: "Pensada para mantener una rutina estable durante el mes.",
  },
  {
    id: 5,
    title: "FULL XFIT",
    price: "$40,00",
    duration: "30 días",
    choices: ["Cross", "Funcional", "Máquinas"],
    summary: "Más libertad para vivir la experiencia NovaXFit al máximo.",
    detailOne: "Combina diferentes estilos de entrenamiento en un solo plan.",
    detailTwo: "Ideal para quienes buscan variedad y rendimiento constante.",
  },
  {
    id: 6,
    title: "FUNCIONAL",
    price: "$28,00",
    duration: "30 días",
    choices: ["Funcional", "Circuitos", "Acondicionamiento"],
    summary: "Enfocado en movimiento, resistencia y mejora integral.",
    detailOne: "Una opción clara para trabajar condición física y energía.",
    detailTwo: "Perfecto para quienes prefieren sesiones dinámicas y activas.",
  },
  {
    id: 7,
    title: "CROSS",
    price: "$35,00",
    duration: "30 días",
    choices: ["Cross", "WOD", "Resistencia"],
    summary: "Pensado para quienes buscan intensidad y superación constante.",
    detailOne: "Entrena con enfoque en potencia, técnica y disciplina.",
    detailTwo: "Una alternativa fuerte para avanzar con objetivos retadores.",
  },
  {
    id: 9,
    title: "PERSONAL TRAINER",
    price: "$100,00",
    duration: "30 días",
    choices: ["Coach 1 a 1", "Seguimiento", "Plan guiado"],
    summary: "Atención personalizada para un avance más preciso.",
    detailOne: "Recibe acompañamiento cercano según tu meta y nivel.",
    detailTwo: "Una experiencia premium para entrenar con dirección.",
  },
  {
    id: 10,
    title: "PARTNER PASS",
    price: "$18,00",
    duration: "Acceso especial",
    choices: ["Beneficios", "Descuentos", "Experiencia NovaXFit"],
    summary: "Acceso pensado para aprovechar alianzas y beneficios exclusivos.",
    detailOne: "Ideal para conectar con promociones y ventajas especiales.",
    detailTwo: "Una puerta de entrada a la comunidad y sus partners.",
  },
];

function PlanCard({ plan, mobile = false }) {
  const getWhatsAppLink = (planTitle) => {
    const phone = "593985224301";
    const message = `Hola, quiero información sobre el plan ${planTitle}.`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  };

  return (
    <MotionBox
      initial={{ opacity: 0, y: 26, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 18, scale: 0.98 }}
      whileHover={mobile ? {} : { y: -8 }}
      transition={{ duration: 0.42, ease: "easeOut" }}
      sx={{
        width: "100%",
        maxWidth: mobile ? 300 : 306,
        mx: "auto",
        position: "relative",
        overflow: "hidden",
        borderRadius: { xs: "24px", md: "28px" },
        p: { xs: 2, md: 2.35 },
        minHeight: { xs: 430, md: 390 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.045) 100%)",
        border: "1px solid rgba(255,255,255,0.1)",
        backdropFilter: "blur(12px)",
        boxShadow: "0 20px 50px rgba(0,0,0,0.28)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(140deg, rgba(229,9,20,0.10) 0%, rgba(255,255,255,0.02) 35%, rgba(255,255,255,0) 100%)",
          pointerEvents: "none",
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            mb: 1.45,
          }}
        >
          <Chip
            icon={<AccessTimeRoundedIcon sx={{ fontSize: "17px !important" }} />}
            label={plan.duration}
            sx={{
              height: 30,
              borderRadius: "999px",
              bgcolor: "rgba(229,9,20,0.14)",
              border: "1px solid rgba(229,9,20,0.22)",
              color: "#fff",
              fontWeight: 700,
              "& .MuiChip-label": { px: 1 },
            }}
          />

          <Typography
            sx={{
              fontSize: mobile ? 27 : 28,
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: -1,
              textAlign: "right",
            }}
          >
            {plan.price}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: mobile ? 19 : 21,
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: -0.6,
            mb: 1.45,
            minHeight: mobile ? "auto" : 46,
          }}
        >
          {plan.title}
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 1.6 }}>
          {plan.choices.map((item) => (
            <Chip
              key={item}
              label={item}
              sx={{
                height: 29,
                borderRadius: "999px",
                bgcolor: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.9)",
                fontWeight: 600,
                fontSize: 11.6,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          ))}
        </Box>

        <Box sx={{ display: "grid", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <SellRoundedIcon
              sx={{ fontSize: 17, mt: "2px", color: "rgba(255,255,255,0.72)" }}
            />
            <Typography
              sx={{
                color: "rgba(255,255,255,0.76)",
                fontSize: 13.2,
                lineHeight: 1.65,
                fontWeight: 500,
              }}
            >
              {plan.summary}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              color: "rgba(255,255,255,0.76)",
            }}
          >
            <FitnessCenterRoundedIcon sx={{ fontSize: 17, mt: "2px" }} />
            <Typography sx={{ fontSize: 13.2, lineHeight: 1.65, fontWeight: 500 }}>
              {plan.detailOne}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              color: "rgba(255,255,255,0.76)",
            }}
          >
            <AutoAwesomeRoundedIcon sx={{ fontSize: 17, mt: "2px" }} />
            <Typography sx={{ fontSize: 13.2, lineHeight: 1.65, fontWeight: 500 }}>
              {plan.detailTwo}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Button
        component="a"
        href={getWhatsAppLink(plan.title)}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Consultar plan ${plan.title}`}
        endIcon={<ArrowOutwardRoundedIcon aria-hidden="true" />}
        sx={{
          position: "relative",
          zIndex: 1,
          mt: 2.1,
          textTransform: "none",
          minHeight: 46,
          borderRadius: "16px",
          fontSize: 14,
          fontWeight: 700,
          color: "#fff",
          background:
            "linear-gradient(135deg, #ff3e4a 0%, #e50914 60%, #b40010 100%)",
          boxShadow: "0 16px 30px rgba(229,9,20,0.22)",
          "&:hover": {
            background:
              "linear-gradient(135deg, #ff5c67 0%, #ff2230 60%, #c60012 100%)",
          },
        }}
      >
        Consultar plan
      </Button>
    </MotionBox>
  );
}

function DotButton({ active, label, onClick, activeWidth = 24, inactiveWidth = 8 }) {
  return (
    <Box
      component="button"
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      sx={{
        width: 44,
        height: 44,
        minWidth: 44,
        minHeight: 44,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: "none",
        p: 0,
        m: 0,
        borderRadius: 999,
        backgroundColor: "transparent",
        cursor: "pointer",
        outline: "none",
        appearance: "none",
        WebkitAppearance: "none",
        MozAppearance: "none",
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
        "&:focus-visible": {
          boxShadow: "0 0 0 3px rgba(255,255,255,0.28)",
        },
      }}
    >
      <Box
        sx={{
          width: active ? activeWidth : inactiveWidth,
          height: 8,
          borderRadius: 999,
          bgcolor: active ? "#e50914" : "rgba(255,255,255,0.2)",
          transition: "all .25s ease",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
}

export default function PlansSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileIndex, setMobileIndex] = useState(0);
  const [desktopPage, setDesktopPage] = useState(0);

  const desktopPages = useMemo(() => {
    const grouped = [];
    for (let i = 0; i < plans.length; i += 3) {
      grouped.push(plans.slice(i, i + 3));
    }
    return grouped;
  }, []);

  const activePlan = plans[mobileIndex];
  const activeDesktopPlans = desktopPages[desktopPage];

  const nextMobile = () => {
    setMobileIndex((prev) => (prev + 1) % plans.length);
  };

  const prevMobile = () => {
    setMobileIndex((prev) => (prev - 1 + plans.length) % plans.length);
  };

  const nextDesktop = () => {
    setDesktopPage((prev) => (prev + 1) % desktopPages.length);
  };

  const prevDesktop = () => {
    setDesktopPage((prev) => (prev - 1 + desktopPages.length) % desktopPages.length);
  };

  return (
    <Box
      component="section"
      sx={{
        bgcolor: "#050505",
        color: "#fff",
        py: { xs: 5.5, md: 9 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -140,
          left: -120,
          width: 340,
          height: 340,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(229,9,20,0.17) 0%, rgba(229,9,20,0) 72%)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          right: -160,
          bottom: -140,
          width: 380,
          height: 380,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,62,74,0.12) 0%, rgba(255,62,74,0) 72%)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          width: { xs: "92%", md: "86%" },
          maxWidth: 1380,
          mx: "auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            maxWidth: 880,
            mx: "auto",
            textAlign: "center",
            mb: { xs: 3.5, md: 6.5 },
          }}
        >
          <Typography
            sx={{
              fontSize: {
                xs: "clamp(28px,7vw,36px)",
                md: "clamp(40px,4vw,54px)",
              },
              fontWeight: 700,
              lineHeight: 1.02,
              letterSpacing: -1.2,
              mb: 1.2,
            }}
          >
            Elige el plan que mejor va contigo
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.72)",
              fontSize: { xs: 14, md: 17 },
              lineHeight: 1.75,
              fontWeight: 500,
              maxWidth: 760,
              mx: "auto",
            }}
          >
            Conoce rápidamente el precio, la duración y las modalidades que
            puedes escoger dentro de NovaXFit.
          </Typography>
        </Box>

        {isMobile ? (
          <Box sx={{ maxWidth: 320, mx: "auto" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 1.8,
              }}
            >
              <IconButton
                aria-label="Plan anterior"
                onClick={prevMobile}
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.12)",
                  },
                }}
              >
                <ChevronLeftRoundedIcon aria-hidden="true" />
              </IconButton>

              <Typography
                sx={{
                  minWidth: 84,
                  textAlign: "center",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.75)",
                }}
              >
                {mobileIndex + 1} / {plans.length}
              </Typography>

              <IconButton
                aria-label="Plan siguiente"
                onClick={nextMobile}
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.12)",
                  },
                }}
              >
                <ChevronRightRoundedIcon aria-hidden="true" />
              </IconButton>
            </Box>

            <AnimatePresence mode="wait">
              <PlanCard key={activePlan.id} plan={activePlan} mobile />
            </AnimatePresence>

            <Box
              sx={{
                mt: 1.8,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 0.2,
                flexWrap: "wrap",
              }}
            >
              {plans.map((item, index) => (
                <DotButton
                  key={item.id}
                  active={index === mobileIndex}
                  label={`Ver plan ${item.title}`}
                  onClick={() => setMobileIndex(index)}
                  activeWidth={24}
                  inactiveWidth={8}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ maxWidth: 1080, mx: "auto" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.2,
                mb: 2.2,
              }}
            >
              <IconButton
                aria-label="Página anterior"
                onClick={prevDesktop}
                sx={{
                  width: 46,
                  height: 46,
                  bgcolor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.12)",
                  },
                }}
              >
                <ChevronLeftRoundedIcon aria-hidden="true" />
              </IconButton>

              <Typography
                sx={{
                  minWidth: 98,
                  textAlign: "center",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.75)",
                }}
              >
                {desktopPage + 1} / {desktopPages.length}
              </Typography>

              <IconButton
                aria-label="Página siguiente"
                onClick={nextDesktop}
                sx={{
                  width: 46,
                  height: 46,
                  bgcolor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.12)",
                  },
                }}
              >
                <ChevronRightRoundedIcon aria-hidden="true" />
              </IconButton>
            </Box>

            <AnimatePresence mode="wait">
              <MotionBox
                key={desktopPage}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "stretch",
                  gap: 2.4,
                  flexWrap: "nowrap",
                  width: "100%",
                }}
              >
                {activeDesktopPlans.map((plan) => (
                  <PlanCard key={plan.id} plan={plan} />
                ))}
              </MotionBox>
            </AnimatePresence>

            <Box
              sx={{
                mt: 2.1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 0.2,
                flexWrap: "wrap",
              }}
            >
              {desktopPages.map((_, index) => (
                <DotButton
                  key={index}
                  active={index === desktopPage}
                  label={`Ver página ${index + 1} de planes`}
                  onClick={() => setDesktopPage(index)}
                  activeWidth={26}
                  inactiveWidth={9}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}